import React, { Children, useMemo, useRef } from "react";
import type { FC, PropsWithChildren, ReactElement } from "react";

import cn from "classnames";

import ScrollContainer from "../../../helpers/wrappers/ScrollContainer/ScrollContainer";
import { extractProps, getDisplayName } from "../../../utils/react";
import { ChipChoice } from "../ChipChoice";

import type {
    ScrollContainerProps,
    ScrollContainerRef,
} from "../../../helpers/wrappers/ScrollContainer/ScrollContainer";
import type { PropsWithBase } from "../../../types/react";

import classes from "./ChipsView.styl";

const CHIP_CHOICE_TEST_ID = "chip-choice-test-id";
export const CHIPS_VIEW_BUTTON_SCROLL_ID = "chips_view_button_scroll_id";
const CHIPS_VIEW_DISPLAY_NAME = "ChipsView";

export type IChipView = {
    /** Отвечает за включение чипса, через который настраиваются все фильтры */
    allFilters?: boolean;
    /** Отвечает за наличие кнопок скролла */
    scrollButton?: boolean;
    /** Отвечает за настройку wrap content внутри контейнера. При метке false внутри chipsView — горизонтальный скролл */
    wrapContent?: boolean;
    /** Отвечает за возможность множественного выбора chip. При false можно выбрать только один chip */
    multiselect?: boolean;
    /** Отвечает за текст скринридера левой кнопки */
    labelLeft?: string;
    /** Отвечает за текст скринридера правой кнопки */
    labelRight?: string;
};

export type ChipViewProps = PropsWithBase<IChipView>;

/**
 * Базовый компонент, при помощи них можно отфильтровать контент, тэнуть пользователя или отобразить подсказку. Контейнер для компонентов Chips
 *
 * <a href="https://www.figma.com/design/LYZAXE33pUg2cSL5mmUHYq/%F0%9F%8D%91-Buttons-%26-Tabs-%26-Chips?m=auto&node-id=2-3275&t=3ryRFv5yEABAv4kK-1" target="_blank">Спека</a>
 * @constructor
 */

export const ChipsView: FC<PropsWithChildren<ChipViewProps>> = (props) => {
    const { customColorsStyles } = extractProps(props, ["customColorsStyles"]);
    const {
        allFilters = true,
        scrollButton = true,
        wrapContent = false,
        view = "primary",
        labelLeft,
        labelRight,
        children,
        "data-tsid": tsid,
    } = props;

    const [quantity, isTypeChipChoice, selected] = useMemo(() => {
        let isTypeChipChoice = false;
        let selected = false;
        const quantity = Children.toArray(children).reduce<number>(
            (acc, child) => {
                isTypeChipChoice = getDisplayName(child) === "ChipChoice";
                if (
                    getDisplayName(child) !== "ChipChoice" &&
                    getDisplayName(child) !== "ChipUser" &&
                    getDisplayName(child) !== "ChipSuggest"
                ) {
                    throw new Error(
                        "В компоненте ChipsView могут быть только компоненты семейства Chips",
                    );
                }
                if ((child as ReactElement).props.selected) {
                    selected = true;
                    return acc + 1;
                }
                return acc;
            },
            0,
        );
        return [quantity, isTypeChipChoice, selected];
    }, [children]);

    const chipViewClassName = cn(classes["chips-container"], {
        [classes[`__nowrap`]]: !wrapContent,
    });

    const buttonScrollRef = useRef<ScrollContainerRef>(null);

    const buttonScrollProps = {
        "data-tsid": CHIPS_VIEW_BUTTON_SCROLL_ID,
        customColorsStyles,
        buttonRef: buttonScrollRef,
        labelLeft,
        labelRight,
        view,
    } as ScrollContainerProps;

    const showWithScrollButton = scrollButton && !wrapContent;

    return (
        <div
            className={classes["container"]}
            data-tsid={tsid}
            data-uikit={CHIPS_VIEW_DISPLAY_NAME}
        >
            {showWithScrollButton ? (
                <ScrollContainer {...buttonScrollProps}>
                    <div className={chipViewClassName}>
                        {allFilters && isTypeChipChoice && (
                            <ChipChoice
                                data-tsid={CHIP_CHOICE_TEST_ID}
                                type="icon"
                                selected={selected}
                            >
                                <ChipChoice.MainIcon />
                                <ChipChoice.Counter quantity={quantity} />
                            </ChipChoice>
                        )}
                        {children}
                    </div>
                </ScrollContainer>
            ) : (
                <div className={chipViewClassName}>
                    {allFilters && isTypeChipChoice && (
                        <ChipChoice
                            data-tsid={CHIP_CHOICE_TEST_ID}
                            type="icon"
                            selected={selected}
                        >
                            <ChipChoice.MainIcon />
                            <ChipChoice.Counter quantity={quantity} />
                        </ChipChoice>
                    )}
                    {children}
                </div>
            )}
        </div>
    );
};

ChipsView.displayName = CHIPS_VIEW_DISPLAY_NAME;

export default ChipsView;
